import type { ScorecardKey } from "@shared/scorecard";

/**
 * Central register of the quantitative-evidence tokens surfaced in annotation documents.
 *
 * The design deliberately recognises a *closed vocabulary* of statistical patterns rather than
 * "anything that looks important" — each recognizer has a tight signature with a near-zero
 * false-positive rate on prose. Parenthesis membership is intentionally NOT a trigger: the token
 * pattern is the signal, and headline effect sizes routinely sit outside parentheses. See the
 * design discussion and `recognizers.test.ts` for the false-positive guardrail corpus.
 *
 * Every recognizer names the scorecard field(s) it is evidence for (`ScorecardKey`, the same
 * vocabulary the annotation panels use), which is what lets the tooltip do workflow work
 * ("evidence for Statistical rigor") rather than merely naming the token.
 */

/** Visual grouping for token colour — a category, not an ordered tier. The precise slug lives in the tooltip. */
export type EvidenceTokenCategory = "magnitude" | "significance";

/** Stable slug identifying a token type in the register. Distinct namespace from scorecard field keys. */
export type EvidenceTokenSlug =
  "sample_size" | "p_value" | "effect_size" | "correlation" | "confidence_interval";

/** One recognizer in the register: a tight pattern plus the metadata its tooltip presents. */
export interface EvidenceRecognizer {
  slug: EvidenceTokenSlug;
  category: EvidenceTokenCategory;
  /** Human noun phrase for the tooltip heading, e.g. "sample size". */
  label: string;
  /** The scorecard field(s) this token is evidence for — real `ScorecardKey`s, rendered via `fieldLabel`. */
  fields: readonly ScorecardKey[];
  /** Global-flagged pattern matching exactly one token. Tolerant of the mangled whitespace PMC extraction produces. */
  pattern: RegExp;
}

/**
 * The register. Tier-1 recognizers only — patterns with a near-zero false-positive rate.
 * Cohort/group-label detection (e.g. `TB-SP`) is deliberately excluded until this proves its value.
 */
export const EVIDENCE_RECOGNIZERS: readonly EvidenceRecognizer[] = [
  {
    slug: "sample_size",
    category: "magnitude",
    label: "sample size",
    fields: ["sample_size", "statistical_power"],
    // n = 55, N= 1,024. Word boundary before n avoids matching the n inside words like "region".
    pattern: /\b[nN]\s*=\s*\d[\d,]*\b/g,
  },
  {
    slug: "p_value",
    category: "significance",
    label: "p-value",
    fields: ["statistical_power", "statistical_rigor"],
    // p = 0.003, p< 0.0001, P ≤ .05, p = 1.0.
    pattern: /\b[pP]\s*(?:=|<|>|≤|≥)\s*(?:\d+(?:\.\d+)?|\.\d+)/g,
  },
  {
    slug: "effect_size",
    category: "magnitude",
    label: "effect size",
    fields: ["statistical_rigor"],
    // OR = 1.5, aHR= 0.80, RR = 2.1. Case-sensitive so the English word "or" never matches.
    pattern: /\b(?:aOR|aHR|OR|HR|RR)\s*=\s*[−-]?\d+(?:\.\d+)?/g,
  },
  {
    slug: "correlation",
    category: "magnitude",
    label: "correlation coefficient",
    fields: ["statistical_rigor"],
    // r = 0.42, r = −0.31, ρ = 0.5, bounded to [-1, 1]. A negative lookbehind (not `\b`) guards the
    // start: `\b` is ASCII-only and would never fire before ρ.
    pattern: /(?<![A-Za-z])[rρ]\s*=\s*[−-]?(?:0?\.\d+|1(?:\.0+)?)\b/g,
  },
  {
    slug: "confidence_interval",
    category: "significance",
    label: "confidence interval",
    fields: ["statistical_rigor"],
    // 95% CI, 95 % CI, 99% confidence interval.
    pattern: /\b\d{2}\s*%\s*(?:CI|confidence interval)\b/gi,
  },
];

/** The register keyed by slug, for O(1) lookup at render time. */
export const RECOGNIZER_BY_SLUG: Readonly<Record<EvidenceTokenSlug, EvidenceRecognizer>> =
  Object.fromEntries(EVIDENCE_RECOGNIZERS.map((r) => [r.slug, r])) as Record<
    EvidenceTokenSlug,
    EvidenceRecognizer
  >;

/** A resolved token occurrence in a run of text. */
export interface EvidenceTokenMatch {
  start: number;
  end: number;
  recognizer: EvidenceRecognizer;
}

/**
 * Finds every recognised evidence token in a run of plain text, with overlaps resolved
 * (earliest start wins; on a tie the longer match wins). Returned matches are non-overlapping
 * and sorted by start offset.
 */
export function findEvidenceTokens(text: string): EvidenceTokenMatch[] {
  const candidates: EvidenceTokenMatch[] = [];
  for (const recognizer of EVIDENCE_RECOGNIZERS) {
    for (const match of text.matchAll(recognizer.pattern)) {
      if (match.index === undefined) continue;
      candidates.push({ start: match.index, end: match.index + match[0].length, recognizer });
    }
  }
  candidates.sort((a, b) => a.start - b.start || b.end - b.start - (a.end - a.start));

  const resolved: EvidenceTokenMatch[] = [];
  let lastEnd = -1;
  for (const candidate of candidates) {
    if (candidate.start >= lastEnd) {
      resolved.push(candidate);
      lastEnd = candidate.end;
    }
  }
  return resolved;
}
